import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from './store.js';
import { buildModel } from './lib/model.js';
import { appendLog } from './lib/state.js';
import { todayIn } from './lib/calendar.js';
import { setNumberLocale } from './lib/money.js';
import { Icon } from './components/ui.jsx';
import Overview from './components/Overview.jsx';
import Months from './components/Months.jsx';
import Budget from './components/Budget.jsx';
import Pay from './components/Pay.jsx';
import Savings from './components/Savings.jsx';
import Settings from './components/Settings.jsx';
import ReviewDialog from './components/ReviewDialog.jsx';
import MonthDialog from './components/MonthDialog.jsx';
import SignIn from './components/SignIn.jsx';

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export const PAGES = [
  { id: 'overview', label: 'Overview' },
  { id: 'months', label: 'Months' },
  { id: 'budget', label: 'Budget' },
  { id: 'pay', label: 'Pay' },
  { id: 'savings', label: 'Savings' },
  { id: 'settings', label: 'Settings' },
];

// Where to send someone who opened the app over plain http when a secure address exists (null: stay)
export function secureRedirect(loc, publicUrl) {
  if (!publicUrl || !/^https:\/\//.test(publicUrl)) return null;
  if (loc.protocol !== 'http:' || ['localhost', '127.0.0.1', '[::1]'].includes(loc.hostname)) return null;
  return `${publicUrl.replace(/\/+$/, '')}${loc.pathname || '/'}${loc.search || ''}${loc.hash || ''}`;
}

const pageFromHash = () => {
  let id = (typeof window !== 'undefined' ? window.location.hash : '').replace(/^#\/?/, '').split('/')[0];
  if (id === 'income') id = 'pay'; // old address
  return PAGES.some((p) => p.id === id) ? id : 'overview';
};

function useToday(fixed, tz) {
  const [today, setToday] = useState(() => fixed || todayIn(tz));
  useEffect(() => {
    if (fixed) { setToday(fixed); return undefined; }
    const tick = () => setToday(todayIn(tz));
    tick();
    const t = setInterval(tick, 60000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', tick); };
  }, [fixed, tz]);
  return today;
}

const SYNC_TEXT = {
  local: 'On this device only',
  connecting: 'Connecting…',
  online: 'Saved',
  saving: 'Saving…',
  offline: 'Offline — changes kept here',
  'signed-out': 'Signed out',
};

export default function App({ api, today: fixedToday = null, storagePrefix = 'js' }) {
  const [toasts, setToasts] = useState([]);
  const toastId = useRef(0);
  const toast = useCallback((msg, { kind = 'info', action = null, ms = 5000 } = {}) => {
    const id = ++toastId.current;
    setToasts((t) => [...t.slice(-3), { id, msg, kind, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ms);
  }, []);

  // Sign-in (only when the server asks for it)
  const [auth, setAuth] = useState({ checked: !api, required: false, google: null, user: null });
  const checkAuth = useCallback(async () => {
    if (!api) return;
    try {
      const cfg = await api.getConfig();
      const target = secureRedirect(window.location, cfg?.publicUrl);
      if (target) { window.location.replace(target); return; }
      if (!cfg?.auth?.required) { setAuth({ checked: true, required: false, google: null, user: null }); return; }
      try {
        const me = await api.me();
        setAuth({ checked: true, required: true, google: cfg.auth.google, user: me });
      } catch {
        setAuth({ checked: true, required: true, google: cfg.auth.google, user: null });
      }
    } catch {
      setAuth({ checked: true, required: false, google: null, user: null }); // server unreachable: work offline
    }
  }, [api]);
  useEffect(() => { checkAuth(); }, [checkAuth]);
  const blocked = auth.required && !auth.user;

  const store = useStore({ api: auth.checked && !blocked ? api : null, prefix: storagePrefix, onNotice: (m) => toast(m, { ms: 9000 }) });
  const data = store.data;
  useEffect(() => { setNumberLocale(data.settings.numberLocale); }, [data.settings.numberLocale]);

  const today = useToday(fixedToday, data.settings.timezone);

  // Linked spreadsheets
  const [sheets, setSheets] = useState({});
  const loadSheets = useCallback(() => { if (api && auth.checked && !blocked) api.getSheets().then(setSheets).catch(() => {}); }, [api, auth.checked, blocked]);
  useEffect(() => {
    loadSheets();
    if (!api || !auth.checked || blocked) return undefined;
    return api.subscribe((msg) => { if (msg?.sheets) loadSheets(); });
  }, [api, auth.checked, blocked, loadSheets]);

  const model = useMemo(() => {
    try {
      return buildModel(data, { today, sheets });
    } catch (e) {
      console.error(e); // eslint-disable-line no-console
      return buildModel(data, { today, sheets: {} });
    }
  }, [data, today, sheets]);

  // Who is using this device
  const [meLocal, setMeLocal] = useState(() => { try { return localStorage.getItem(`${storagePrefix}_me`) || null; } catch { return null; } });
  const meByEmail = auth.user?.email ? data.people.find((p) => p.email && p.email === auth.user.email)?.id : null;
  const me = meByEmail || (data.people.some((p) => p.id === meLocal) ? meLocal : null);
  const setMe = (id) => { setMeLocal(id); try { localStorage.setItem(`${storagePrefix}_me`, id); } catch {} };

  const commit = useCallback((label, fn, { undoToast = false } = {}) => {
    const res = store.commit(label, (s) => appendLog(fn(s), { by: me, label }));
    if (res.error) toast(res.error, { kind: 'error' });
    else if (undoToast && !res.unchanged) toast(label, { action: { label: 'Undo', fn: () => store.undo() } });
    return res;
  }, [store, me, toast]);

  const [page, setPage] = useState(pageFromHash);
  useEffect(() => {
    const onHash = () => setPage(pageFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const go = useCallback((id) => {
    setPage(id);
    if (window.location.hash !== `#/${id}`) window.history.pushState(null, '', `#/${id}`);
    window.scrollTo?.(0, 0);
  }, []);

  const [review, setReview] = useState(null); // { month, personId }
  const [monthOpen, setMonthOpen] = useState(null);

  // Undo/redo shortcuts (not while typing in a box: the box has its own undo)
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target?.isContentEditable) return;
      if (document.querySelector('.scrim')) return;
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === 'z' && !e.shiftKey) { e.preventDefault(); const l = store.undo(); if (l) toast(`Undid: ${l}`); }
      else if ((k === 'z' && e.shiftKey) || k === 'y') { e.preventDefault(); const l = store.redo(); if (l) toast(`Redid: ${l}`); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [store, toast]);

  const ctx = {
    data, model, commit, store, today, me, setMe, api, sheets, loadSheets, toast, go, auth, checkAuth,
    openReview: (month, personId) => setReview({ month, personId }),
    openMonth: (month) => setMonthOpen(month),
    person: (id) => data.people.find((p) => p.id === id),
  };

  if (!auth.checked) return <div className="app"><div className="empty">Loading…</div></div>;
  if (blocked) return <Ctx.Provider value={ctx}><SignIn /></Ctx.Provider>;
  if (!store.ready) return <div className="app"><div className="empty">Loading…</div></div>;

  const pendingCount = model.pending.length;
  return (
    <Ctx.Provider value={ctx}>
      <div className="app">
        <header className="topbar">
          <div className="brand">
            <svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true"><rect width="32" height="32" rx="8" fill="#1e2024" /><path d="M8 20c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="none" stroke="#3987e5" strokeWidth="3" strokeLinecap="round" /><circle cx="16" cy="20" r="3" fill="#3987e5" /></svg>
            <span className="name">Joint Savings</span>
          </div>
          <nav className="nav" aria-label="Pages">
            {PAGES.map((p) => (
              <button key={p.id} type="button" aria-current={page === p.id ? 'page' : undefined} onClick={() => go(p.id)}>
                {p.label}
                {p.id === 'months' && pendingCount > 0 && <span className="count" aria-label={`${pendingCount} to review`}>{pendingCount}</span>}
              </button>
            ))}
          </nav>
          <div className="top-actions">
            <span className={`sync ${store.sync}`} title={store.lastSaved ? `Last saved ${new Date(store.lastSaved).toLocaleTimeString()}` : ''}>
              <span className="dot" /> <span className="hide-sm">{SYNC_TEXT[store.sync] || store.sync}</span>
            </span>
            <button type="button" className="btn ghost icon" aria-label={store.undoLabel ? `Undo: ${store.undoLabel}` : 'Undo'} title={store.undoLabel ? `Undo: ${store.undoLabel} (Ctrl+Z)` : 'Nothing to undo'} disabled={!store.canUndo} onClick={() => { const l = store.undo(); if (l) toast(`Undid: ${l}`); }}><Icon name="undo" /></button>
            <button type="button" className="btn ghost icon" aria-label={store.redoLabel ? `Redo: ${store.redoLabel}` : 'Redo'} title={store.redoLabel ? `Redo: ${store.redoLabel} (Ctrl+Shift+Z)` : 'Nothing to redo'} disabled={!store.canRedo} onClick={() => { const l = store.redo(); if (l) toast(`Redid: ${l}`); }}><Icon name="redo" /></button>
          </div>
        </header>
        <main>
          {page === 'overview' && <Overview />}
          {page === 'months' && <Months />}
          {page === 'budget' && <Budget />}
          {page === 'pay' && <Pay />}
          {page === 'savings' && <Savings />}
          {page === 'settings' && <Settings />}
        </main>
        {review && <ReviewDialog month={review.month} personId={review.personId} onClose={() => setReview(null)} />}
        {monthOpen && !review && <MonthDialog month={monthOpen} onClose={() => setMonthOpen(null)} />}
        <div className="toasts" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`toast ${t.kind}`} role={t.kind === 'error' ? 'alert' : 'status'}>
              {t.kind === 'error' && <Icon name="alert" />}
              <span className="msg">{t.msg}</span>
              {t.action && <button type="button" className="btn small" onClick={() => { t.action.fn(); setToasts((x) => x.filter((y) => y.id !== t.id)); }}>{t.action.label}</button>}
              <button type="button" className="btn ghost icon small" aria-label="Dismiss" onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}><Icon name="close" size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </Ctx.Provider>
  );
}
